/**
 * Home Page Component
 * Main landing page with tools and resources tiles
 */

import React from 'react';
import { Tile } from '../components/ui';
import { isResellerOrStaff, isInternalStaff } from '../config/navMenu';
import './Home.css';

const Home = ({ userRole, earlyAccess = false }) => {
  const isRapidFusionInternal = isInternalStaff(userRole);
  const canOpenResellerTools = isResellerOrStaff(userRole);
  const resourceTiles = [
    {
      icon: 'speed',
      title: 'Throughput Calibration',
      description: "Advanced web tool for calibrating your extruder's throughput for optimal performance.",
      linkTo: '/calibration',
      linkText: 'Launch Tool',
    },
    {
      icon: 'ruler',
      title: 'E-Steps Calibration Tool',
      description: 'Precision calibration tool for setting extruder motor steps per millimeter to ensure accurate material flow.',
      linkTo: '/esteps',
      linkText: 'Launch Tool',
    },
    {
      icon: 'thermal',
      title: 'CTE Toolkit',
      description: 'All-in-one thermal expansion suite for AM and composites. Includes CTE calculator, build planner with cycle simulation, mould compensation, 3D distortion mapping, material library, and PDF export.',
      linkTo: '/cte-calculator',
      linkText: 'Launch CTE Toolkit',
    },
    {
      icon: 'robot-arm',
      title: 'Part Alignment',
      description: 'Align CAD parts to real-world robot coordinates and manage part stock. Opens the Part Alignment application directly within the Knowledge Centre.',
      linkTo: '/part-alignment',
      linkText: 'Launch Tool',
    },
    {
      icon: 'inspect',
      title: 'Design Check',
      description: 'Upload a STEP file for automated design-for-manufacture analysis against the PE320 large-format printing process, with an interactive 3D viewer, design guide, and AI advisor.',
      linkTo: '/design-check',
      linkText: 'Launch Tool',
    },
    {
      icon: 'wrench',
      title: 'Technical Support & Self-Commissioning',
      description: 'AI assistant for Epicurus hardware commissioning. Search live docs, configs, and images, and get step-by-step support for PE320, robots, drives, and site setup.',
      linkTo: '/tech-support',
      linkText: 'Open Assistant',
    },
    {
      icon: 'building',
      title: 'Reseller Assets',
      description: 'Access marketing materials, product sheets, pricing tools, and sales resources designed specifically for Rapid Fusion resellers.',
      linkTo: '/reseller-assets',
      linkText: 'Access Resources',
    },
  ];

  const trainingTiles = [
    {
      icon: 'graduation-cap',
      title: 'Online Training Courses',
      description: isRapidFusionInternal
        ? 'Work through Operator Training, Reseller Commissioning, and Sales at your own pace. Each course is marked Live, Early access, or Hidden. Sales is for staff and resellers.'
        : earlyAccess
          ? 'Work through the courses. Items marked Pre-release are still being reviewed.'
          : 'Released courses are open. Everything else is listed as Upcoming until we finish review.',
      linkTo: '/field-ops-courses.html',
      linkText: 'View Courses',
    },
    {
      icon: 'book',
      title: 'Manuals & Guides',
      description: isRapidFusionInternal
        ? 'Read a course as one page or download the full Apollo or Zeus cell manual. Each item is marked Live, Early access, or Hidden.'
        : earlyAccess
          ? 'Cell manuals are available. Pre-release items are still being reviewed.'
          : 'Released manuals are open. Pre-release items show as Upcoming until we finish review.',
      linkTo: '/guides',
      linkText: 'View Manuals',
    },
  ];

  const cloudTiles = [
    {
      icon: 'circuit',
      title: 'Machine Gateway',
      description: 'Centralized cloud-based control center for managing and monitoring connected manufacturing machines and equipment across your entire operation.',
      linkTo: '/gateway',
      linkText: 'Launch Gateway',
    },
  ];

  const salesTiles = [
    {
      icon: 'cost-chart',
      title: 'Cost Calculator',
      description: 'Compare tooling vs direct-to-print production costs with AI-powered recommendations.',
      linkTo: '/cost-calculator',
      linkText: 'Launch Calculator',
    },
    {
      icon: 'target',
      title: 'Sales Commission Calculator',
      description: 'Comprehensive commission tracking and calculation tool for the Rapid Fusion sales team. Calculate commissions, track performance metrics, and generate detailed reports.',
      linkTo: '/commission',
      linkText: 'Launch Tool',
    },
    {
      icon: 'partners',
      title: 'Reseller Dashboard',
      description: 'Target-based performance management system for reseller network. Track sales targets, monitor achievements, manage leads, and analyse performance metrics with comprehensive reporting.',
      linkTo: '/reseller-dashboard',
      linkText: 'Launch Dashboard',
    },
  ];

  const resellerSalesTiles = [
    {
      icon: 'tag',
      title: 'Price List & Proposals',
      description: 'Browse the price list, build a quote and turn it into a branded proposal deck. Save drafts, edit the deck online and keep every revision.',
      linkTo: '/price-list',
      linkText: 'Open Price List',
    },
  ];

  // Internal-only tiles (RapidFusion staff only)
  const internalSalesTiles = [
    {
      icon: 'process-flow',
      title: 'Quote to Production',
      description: 'From HubSpot deal to Linear production in four stages. AI drafts the proposal, every STEP file gets an automatic printability check, and a branded Word report is generated for the customer.',
      linkTo: '/quote-to-production',
      linkText: 'Open Quote to Production',
    },
  ];

  const operationsTiles = [
    {
      icon: 'clipboard-check',
      title: 'Field Operations Hub',
      description: 'Central dashboard for all field operations, compliance, and on-site activities.',
      linkTo: '/field-ops',
      linkText: 'Open Hub',
    },
    {
      icon: 'calendar-check',
      title: 'Install Scheduler',
      description: 'Plan robot installs against component lead times. Two-phase FAT and install scheduling on a Gantt calendar.',
      linkTo: '/install-scheduler',
      linkText: 'Open Scheduler',
    },
    {
      icon: 'presentation',
      title: 'Training Delivery',
      description: 'Standard on-site Apollo and Zeus training sequence. Track each customer through the same package.',
      linkTo: '/field-ops-training-delivery',
      linkText: 'Open Delivery',
    },
    {
      icon: 'chat',
      title: 'Customer feedback',
      description: 'Create a feedback link and QR code after an install or training visit. See who has answered.',
      linkTo: '/field-ops-records?tab=feedback',
      linkText: 'Open feedback',
    },
    {
      icon: 'shield-check',
      title: 'Safety Documents',
      description: 'Browse, download, and sign risk assessments, method statements, COSHH, and policies.',
      linkTo: '/field-ops-hs-library',
      linkText: 'Open Library',
    },
    {
      icon: 'procedure',
      title: 'SOP Library',
      description: 'Upload, review, sign, and convert standard operating procedures to visual HTML documents.',
      linkTo: '/field-ops-sop-library',
      linkText: 'Open Library',
    },
    {
      icon: 'printer-3d',
      title: 'Evo 3D Install & Training',
      description: 'Record machine delivery, installation, test print, operator training, and customer sign-off.',
      linkTo: '/field-ops-evo3d-install',
      linkText: 'Open Form',
    },
    {
      icon: 'layers',
      title: 'Rapid Fusion Project',
      description: 'Select deal and products; run FAT, RF Install, and SAT for this project.',
      linkTo: '/field-ops-rf-project',
      linkText: 'Open Project',
    },
    {
      icon: 'folder',
      title: 'Records & Admin',
      description: 'Search submissions, manage templates, and export compliance records.',
      linkTo: '/field-ops-admin',
      linkText: 'Open Admin',
    },
    {
      icon: 'id-badge',
      title: 'Visitor Register',
      description: 'View who is on site, search visitor records, and export for audit.',
      linkTo: '/field-ops-visitor-log',
      linkText: 'Open Register',
    },
    {
      icon: 'factory',
      title: 'Internal Print Jobs',
      description: 'Create print jobs without a deal and push to Linear for production tracking.',
      linkTo: '/internal-jobs',
      linkText: 'Open Jobs',
    },
    {
      icon: 'box',
      title: 'Document Store',
      description: 'Internal document storage for Engineering, Sales, Admin, and Operations. Upload, organise, search, and manage files.',
      linkTo: '/document-store',
      linkText: 'Open Store',
    },
    {
      icon: 'checklist',
      title: 'Linear Tasks',
      description: 'Task management for the engineering and sales teams. Drag-and-drop board for issues, projects, weekly update PDF, team tracker, customer triage, AI assistant and Microsoft 365 calendar.',
      linkTo: '/linear-chatbot',
      linkText: 'Open Tasks',
    },
  ];

  return (
    <div className="rf-home">
      {/* Hero Section */}
      <section className="rf-hero">
        <h1 className="rf-hero-title">
          <i className="fas fa-tools"></i> Rapid Fusion Tools & Resources
        </h1>
        <p className="rf-hero-subtitle">
          Access user guides, calibration tools, and training materials for our advanced 3D printing products.
        </p>
        <div className="rf-home-manuals">
          <p className="rf-home-manuals-kicker">Latest cell manuals</p>
          <div className="rf-home-manuals-actions">
            <a
              className="rf-btn rf-btn-primary"
              href="/field-ops-manual-pdf.html?set=apollo"
              target="manual-pdf-apollo-full"
              rel="noopener"
            >
              <i className="fas fa-file-pdf" aria-hidden="true"></i>
              Download Apollo manual
            </a>
            <a
              className="rf-btn rf-btn-primary"
              href="/field-ops-manual-pdf.html?set=zeus"
              target="manual-pdf-zeus-full"
              rel="noopener"
            >
              <i className="fas fa-file-pdf" aria-hidden="true"></i>
              Download Zeus manual
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="rf-home-content">
        <div className="rf-container">
          {/* Resources Section */}
          <section className="rf-section">
            <h2 className="rf-section-title">Resources</h2>
            <div className="rf-tiles-grid">
              {resourceTiles.filter((tile) => (
                tile.linkTo !== '/tech-support' || canOpenResellerTools
              )).map((tile, index) => (
                <Tile key={index} {...tile} />
              ))}
            </div>
          </section>

          {/* Training Section */}
          <section className="rf-section">
            <h2 className="rf-section-title">Training & Certification</h2>
            <div className="rf-tiles-grid">
              {trainingTiles.map((tile, index) => (
                <Tile key={index} {...tile} />
              ))}
            </div>
          </section>

          {/* Cloud Service Platform Section */}
          <section className="rf-section">
            <h2 className="rf-section-title">Cloud Service Platform</h2>
            <div className="rf-tiles-grid">
              {cloudTiles.map((tile, index) => (
                <Tile key={index} {...tile} />
              ))}
            </div>
          </section>

          {/* Sales Tools Section */}
          <section className="rf-section">
            <h2 className="rf-section-title">Sales Tools</h2>
            <div className="rf-tiles-grid">
              {salesTiles.map((tile, index) => (
                <Tile key={index} {...tile} />
              ))}
              {canOpenResellerTools && resellerSalesTiles.map((tile, index) => (
                <Tile key={`reseller-${index}`} {...tile} />
              ))}
              {isRapidFusionInternal && internalSalesTiles.map((tile, index) => (
                <Tile key={`internal-${index}`} {...tile} />
              ))}
            </div>
          </section>

          {/* Operations & Compliance Section (internal only) */}
          {isRapidFusionInternal && (
            <section className="rf-section">
              <h2 className="rf-section-title">Operations & Compliance</h2>
              <div className="rf-tiles-grid">
                {operationsTiles.map((tile, index) => (
                  <Tile key={index} {...tile} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
